import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Module } from '@plataforma/shared';
import { get } from '../lib/api';
import { useAuth, useMe } from '../lib/auth';
import { can } from '../lib/perms';
import { Avatar } from '../ui';
import { t } from '../lib/i18n';
import { LanguageSwitch } from '../ui/LanguageSwitch';
import { ProductNavSlot } from '../ui/productNav';

export const MODULE_INFO: Record<Module, { label: string; path: string; color: string }> = {
  home: { label: t('Inicio'), path: '/', color: '#5b8bc4' },
  people: { label: t('Personas'), path: '/people', color: '#4f86c6' },
  services: { label: t('Servicios'), path: '/services', color: '#8b7cf0' },
  checkins: { label: t('Check-Ins'), path: '/checkins', color: '#3fae78' },
  giving: { label: t('Donaciones@@módulo'), path: '/giving', color: '#e0a43a' },
  groups: { label: t('Grupos'), path: '/groups', color: '#e8774a' },
  calendar: { label: t('Calendario'), path: '/calendar', color: '#2fb3c9' },
  registrations: { label: t('Inscripciones'), path: '/registrations', color: '#e05c9a' },
  chat: { label: t('Chat'), path: '/chat', color: '#7fb046' },
  publishing: { label: t('Páginas'), path: '/publishing', color: '#b07ce0' },
  admin: { label: t('Administración'), path: '/admin', color: '#9aa7b8' },
};

const PRODUCTS: Module[] = ['people', 'services', 'checkins', 'giving', 'groups', 'calendar', 'registrations', 'publishing'];

/** Sección en la que está la persona según la dirección (Inicio, Mis tareas y Mi cuenta cuentan como Inicio). */
function currentModule(pathname: string): Module {
  const first = pathname.split('/')[1] ?? '';
  const found = (Object.keys(MODULE_INFO) as Module[]).find((m) => m !== 'home' && MODULE_INFO[m].path === `/${first}`);
  return found ?? 'home';
}

/** Cuadrito con la inicial de la sección, en su color. */
function ModuleIcon({ module, size = 22 }: { module: Module; size?: number }) {
  return (
    <span className="mod-icon" style={{ background: MODULE_INFO[module].color, width: size, height: size }} aria-hidden>
      {module === 'home'
        ? <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 64 64"><path d="M32 8v48M18 22h28" stroke="#fff" strokeWidth="9" strokeLinecap="round" /></svg>
        : MODULE_INFO[module].label.charAt(0)}
    </span>
  );
}

/** Menú desplegable que se cierra al tocar fuera o con Escape. */
function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return { open, setOpen, ref };
}

function MenuLink({ to, children, onClick }: { to: string; children: ReactNode; onClick: () => void }) {
  return <Link to={to} className="menu-item" role="menuitem" onClick={onClick}>{children}</Link>;
}

export default function StaffLayout() {
  const me = useMe();
  const { logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const switcher = useDropdown();
  const account = useDropdown();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const slotRef = useCallback((el: HTMLElement | null) => setSlot(el), []);
  const { setOpen: closeSwitcher } = switcher;
  const { setOpen: closeAccount } = account;
  useEffect(() => { closeSwitcher(false); closeAccount(false); }, [loc.pathname, closeSwitcher, closeAccount]);
  const { data: notif } = useQuery({ queryKey: ['notifications'], queryFn: () => get<{ unread: number }>('/api/me/notifications'), refetchInterval: 60_000 });

  const current = currentModule(loc.pathname);
  const modules = PRODUCTS.filter((m) => can(me, m, 'viewer', 'any'));
  const isAdmin = can(me, 'admin', 'manager', null);
  const close = () => switcher.setOpen(false);
  return (
    <ProductNavSlot.Provider value={slot}>
      <div className="app" style={{ ['--prod' as string]: MODULE_INFO[current].color }}>
        <header className="appbar">
          <div className="switcher" ref={switcher.ref}>
            <button type="button" className="switcher-btn" aria-haspopup="menu" aria-expanded={switcher.open} onClick={() => switcher.setOpen((o) => !o)} aria-label={t('Cambiar de sección')}>
              <ModuleIcon module={current} size={26} />
              <span className="switcher-name">{MODULE_INFO[current].label}</span>
              <span aria-hidden className="chev">▾</span>
            </button>
            {switcher.open && (
              <div className="dropdown" role="menu">
                <MenuLink to="/" onClick={close}><ModuleIcon module="home" />{MODULE_INFO.home.label}</MenuLink>
                <MenuLink to="/tareas" onClick={close}><ModuleIcon module="home" />{t('Mis tareas')}</MenuLink>
                {modules.length > 0 && <div className="menu-sep" />}
                {modules.map((m) => <MenuLink key={m} to={MODULE_INFO[m].path} onClick={close}><ModuleIcon module={m} />{MODULE_INFO[m].label}</MenuLink>)}
                <div className="menu-sep" />
                <MenuLink to="/chat" onClick={close}><ModuleIcon module="chat" />{MODULE_INFO.chat.label}</MenuLink>
                {can(me, 'checkins', 'editor', 'any') && <MenuLink to="/kiosko" onClick={close}><ModuleIcon module="checkins" />{t('Abrir kiosko')}</MenuLink>}
                {isAdmin && <MenuLink to="/admin" onClick={close}><ModuleIcon module="admin" />{MODULE_INFO.admin.label}</MenuLink>}
                <div className="menu-sep" />
                <MenuLink to="/portal" onClick={close}><span className="mod-icon ghost" aria-hidden>👥</span>{t('Portal de miembros')}</MenuLink>
                <MenuLink to="/cuenta" onClick={close}><span className="mod-icon ghost" aria-hidden>⚙</span>{t('Mi cuenta')}</MenuLink>
              </div>
            )}
          </div>
          <nav className="product-nav" ref={slotRef} aria-label={t('Secciones de {name}', { name: MODULE_INFO[current].label })} />
          <div className="appbar-right">
            <span className="org-name" title={me.organization?.name}>{me.organization?.name ?? t('Iglesia')}</span>
            <LanguageSwitch />
            <button type="button" className="btn btn-ghost btn-icon" onClick={() => nav('/portal/notificaciones')} aria-label={t('Notificaciones')}>
              🔔{notif && notif.unread > 0 && <span className="badge red">{notif.unread}</span>}
            </button>
            <div className="switcher" ref={account.ref}>
              <button type="button" className="btn btn-ghost avatar-btn" aria-haspopup="menu" aria-expanded={account.open} onClick={() => account.setOpen((o) => !o)} aria-label={t('Mi cuenta')}>
                <Avatar first={me.person.firstName} last={me.person.lastName} />
              </button>
              {account.open && (
                <div className="dropdown right" role="menu">
                  <div className="menu-head">
                    <strong>{me.person.nickname || me.person.firstName} {me.person.lastName}</strong>
                    <span className="small muted">{me.account.email}</span>
                  </div>
                  <div className="menu-sep" />
                  <MenuLink to="/cuenta" onClick={() => account.setOpen(false)}>{t('Mi cuenta')}</MenuLink>
                  <MenuLink to="/portal" onClick={() => account.setOpen(false)}>{t('Portal de miembros')}</MenuLink>
                  <div className="menu-sep" />
                  <button type="button" className="menu-item" role="menuitem" onClick={async () => { await logout(); nav('/entrar'); }}>{t('Salir')}</button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="content" id="contenido">
          <Outlet />
        </main>
      </div>
    </ProductNavSlot.Provider>
  );
}
